/**
 * DaytonaPage (epic-6 §5 / DAY-3) — replaced InstancesPage. Route /daytona.
 *
 * Sandbox CRUD + lifecycle controls (start/stop/archive/fork/snapshot),
 * resource presets, exec console (poll exec results), and links to
 * repo/branch/factory.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import {
  Play,
  Square,
  Archive,
  Trash2,
  GitFork,
  Terminal,
  RefreshCw,
  Box,
  Plus,
  Camera,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DataTable } from '@/components/shared/DataTable';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { EmptyState } from '@/components/shared/EmptyState';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { useToast } from '@/components/shared/Toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useGetSandboxesQuery,
  useCreateSandboxMutation,
  useStartSandboxMutation,
  useStopSandboxMutation,
  useArchiveSandboxMutation,
  useDeleteSandboxMutation,
  useForkSandboxMutation,
  useSnapshotSandboxMutation,
  useExecuteSandboxCommandMutation,
  useRefreshSandboxMutation,
} from '@/store/api/daytonaApi';
import { useGetRepositoriesQuery, useGetBranchesQuery } from '@/store/api/apiSlice';
import { asList } from '@/store/api/daytonaApi';
import type { DaytonaSandbox, CreateSandboxDto } from '@/types';
import { format, formatDistanceToNow } from 'date-fns';
import { apiErrorMessage } from '@/lib/apiError';

// Re-exported for backward compatibility (tests import it from this module).
export { apiErrorMessage };

const STATE_FILTERS: { value: string; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'started', label: 'Started' },
  { value: 'stopped', label: 'Stopped' },
  { value: 'archived', label: 'Archived' },
  { value: 'error', label: 'Error' },
];

const RESOURCE_PRESETS = [
  { label: 'Small (1 CPU / 2 GB / 10 GB)', cpu: 1, memory: 2048, disk: 10240 },
  { label: 'Medium (2 CPU / 4 GB / 20 GB)', cpu: 2, memory: 4096, disk: 20480 },
  { label: 'Large (4 CPU / 8 GB / 40 GB)', cpu: 4, memory: 8192, disk: 40960 },
];

export function DaytonaPage() {
  const toast = useToast();
  const [stateFilter, setStateFilter] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<DaytonaSandbox | null>(null);
  const [forkTarget, setForkTarget] = useState<DaytonaSandbox | null>(null);
  const [execTarget, setExecTarget] = useState<DaytonaSandbox | null>(null);
  const [forkName, setForkName] = useState('');
  const [execCommand, setExecCommand] = useState('');
  const [execTimeout, setExecTimeout] = useState(30);
  const [execOutput, setExecOutput] = useState('');

  const { data: sandboxes, isLoading } = useGetSandboxesQuery();
  const { data: repositories } = useGetRepositoriesQuery();

  const [createSandbox, { isLoading: isCreating }] = useCreateSandboxMutation();
  const [startSandbox] = useStartSandboxMutation();
  const [stopSandbox] = useStopSandboxMutation();
  const [archiveSandbox] = useArchiveSandboxMutation();
  const [deleteSandbox, { isLoading: isDeleting }] = useDeleteSandboxMutation();
  const [forkSandbox, { isLoading: isForking }] = useForkSandboxMutation();
  const [snapshotSandbox, { isLoading: isSnapshotting }] = useSnapshotSandboxMutation();
  const [executeCommand, { isLoading: isExecuting }] = useExecuteSandboxCommandMutation();
  const [refreshSandbox] = useRefreshSandboxMutation();

  const sandboxList = stateFilter === 'all'
    ? (sandboxes as DaytonaSandbox[] | undefined) ?? []
    : ((sandboxes as DaytonaSandbox[] | undefined) ?? []).filter((s) => s.state === stateFilter);
  const repoList = asList<{ _id: string; name: string }>(repositories);

  const columns: ColumnDef<DaytonaSandbox, unknown>[] = [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <Box className="h-4 w-4 text-muted-foreground shrink-0" />
          <div>
            <p className="font-medium text-sm">{row.original.name}</p>
            <p className="font-mono text-[10px] text-muted-foreground">{row.original.sandboxId.slice(0, 16)}…</p>
          </div>
        </div>
      ),
    },
    {
      accessorKey: 'state',
      header: 'State',
      cell: ({ row }) => <StatusBadge status={row.original.state} />,
    },
    {
      id: 'resources',
      header: 'Resources',
      cell: ({ row }) => {
        const r = row.original.resources;
        if (!r) return <span className="text-xs text-muted-foreground">—</span>;
        return (
          <Badge variant="outline" className="font-mono text-xs">
            {r.cpu}C / {Math.round(r.memory / 1024)}G / {Math.round(r.disk / 1024)}G
          </Badge>
        );
      },
    },
    {
      accessorKey: 'repositoryId',
      header: 'Links',
      cell: ({ row }) => {
        const sb = row.original;
        const repo = repoList.find((r) => r._id === sb.repositoryId);
        return (
          <div className="flex items-center gap-1.5">
            {repo && (
              <Link to={`/repositories/${repo._id}`} className="text-xs text-muted-foreground hover:text-primary">
                {repo.name}
              </Link>
            )}
            {sb.branchId && (
              <BranchName repoId={sb.repositoryId} branchId={sb.branchId} />
            )}
            {sb.factoryId && (
              <Link to={`/factories/${sb.factoryId}`} className="text-xs text-muted-foreground hover:text-primary">
                factory
              </Link>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'lastActivityAt',
      header: 'Last Activity',
      cell: ({ row }) =>
        row.original.lastActivityAt ? (
          <span className="text-xs" title={format(new Date(row.original.lastActivityAt), 'PPpp')}>
            {formatDistanceToNow(new Date(row.original.lastActivityAt), { addSuffix: true })}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
    },
    {
      accessorKey: 'createdAt',
      header: 'Created',
      cell: ({ row }) => format(new Date(row.original.createdAt), 'MMM d, yyyy'),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const sb = row.original;
        const isStarted = sb.state === 'started';
        const isStopped = sb.state === 'stopped' || sb.state === 'error';
        return (
          <div className="flex items-center gap-0.5">
            {isStopped && (
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => startSandbox(sb.sandboxId).unwrap().catch((err) => toast.error(`Start failed: ${apiErrorMessage(err, 'Failed to start')}`))} title="Start">
                <Play className="h-4 w-4 text-green-600" />
              </Button>
            )}
            {isStarted && (
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => stopSandbox(sb.sandboxId).unwrap().catch((err) => toast.error(`Stop failed: ${apiErrorMessage(err, 'Failed to stop')}`))} title="Stop">
                <Square className="h-4 w-4 text-orange-600" />
              </Button>
            )}
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => archiveSandbox(sb.sandboxId).unwrap().catch((err) => toast.error(`Archive failed: ${apiErrorMessage(err, 'Failed to archive')}`))} title="Archive">
              <Archive className="h-4 w-4 text-blue-600" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() =>
                snapshotSandbox(sb.sandboxId)
                  .unwrap()
                  .then((r) => toast.success(`Snapshot ${r.snapshotId}`))
                  .catch((err) => toast.error(`Snapshot failed: ${apiErrorMessage(err, 'Snapshot failed')}`))
              }
              disabled={isSnapshotting}
              title="Snapshot"
            >
              <Camera className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setForkTarget(sb); setForkName(''); }} title="Fork">
              <GitFork className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setExecTarget(sb); setExecCommand(''); setExecOutput(''); }} title="Execute Command">
              <Terminal className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => refreshSandbox(sb.sandboxId).unwrap().catch((err) => toast.error(`Refresh failed: ${apiErrorMessage(err, 'Failed to refresh')}`))} title="Refresh">
              <RefreshCw className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setDeleteTarget(sb)} title="Delete">
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        );
      },
    },
  ];

  async function handleFork() {
    if (forkTarget) {
      try {
        await forkSandbox({ sandboxId: forkTarget.sandboxId, name: forkName || undefined }).unwrap();
        toast.success('Sandbox forked');
        setForkTarget(null);
      } catch (err) {
        toast.error(`Fork failed: ${apiErrorMessage(err, 'Fork failed')}`);
      }
    }
  }

  async function handleExecute() {
    if (execTarget && execCommand.trim()) {
      try {
        const result = await executeCommand({
          sandboxId: execTarget.sandboxId,
          command: execCommand,
          timeout: execTimeout,
        }).unwrap();
        const r = result as { exitCode?: number; stdout?: string; stderr?: string };
        const parts: string[] = [];
        if (r.stdout) parts.push(r.stdout.replace(/\n$/, ''));
        if (r.stderr) parts.push(`[stderr] ${r.stderr.replace(/\n$/, '')}`);
        if (typeof r.exitCode === 'number' && r.exitCode !== 0) parts.push(`[exit ${r.exitCode}]`);
        setExecOutput(parts.length ? parts.join('\n') : 'Command executed (no output)');
      } catch (err: unknown) {
        setExecOutput(apiErrorMessage(err, 'Command failed'));
      }
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Daytona Sandboxes</h1>
          <p className="text-sm text-muted-foreground">
            Development sandboxes with lifecycle controls and exec console (DAY-3)
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Create Sandbox
        </Button>
      </div>

      <Tabs value={stateFilter} onValueChange={setStateFilter}>
        <TabsList>
          {STATE_FILTERS.map((f) => (
            <TabsTrigger key={f.value} value={f.value}>{f.label}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <Card>
        <CardContent className="pt-6">
          {isLoading ? (
            <LoadingSpinner className="py-12" />
          ) : sandboxList.length === 0 ? (
            <EmptyState
              title="No sandboxes"
              description="Create a sandbox to get an isolated dev environment"
              icon={<Box className="h-8 w-8" />}
              action={<Button onClick={() => setCreateOpen(true)}><Plus className="mr-1 h-4 w-4" /> Create Sandbox</Button>}
            />
          ) : (
            <DataTable columns={columns} data={sandboxList} />
          )}
        </CardContent>
      </Card>

      {/* Create dialog */}
      <CreateSandboxDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={() => toast.success('Sandbox created')}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Delete Sandbox"
        description={`Delete sandbox "${deleteTarget?.name ?? deleteTarget?.sandboxId}"? This cannot be undone.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={async () => {
          if (deleteTarget) {
            try {
              await deleteSandbox(deleteTarget.sandboxId).unwrap();
              setDeleteTarget(null);
            } catch (err) {
              toast.error(`Delete failed: ${apiErrorMessage(err, 'Failed to delete sandbox')}`);
            }
          }
        }}
        isLoading={isDeleting}
      />

      <Dialog open={!!forkTarget} onOpenChange={(open) => !open && setForkTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Fork Sandbox</DialogTitle>
            <DialogDescription>Create a copy of sandbox "{forkTarget?.name}"</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="forkName">New Sandbox Name (optional)</Label>
              <Input id="forkName" placeholder="forked-sandbox" value={forkName} onChange={(e) => setForkName(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setForkTarget(null)}>Cancel</Button>
            <Button onClick={handleFork} disabled={isForking}>{isForking ? 'Forking...' : 'Fork'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!execTarget} onOpenChange={(open) => !open && setExecTarget(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Terminal className="h-5 w-5" />
              Execute Command
            </DialogTitle>
            <DialogDescription>Run a shell command in sandbox "{execTarget?.name}"</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="execCommand">Command</Label>
              <Input
                id="execCommand"
                placeholder="ls -la /workspace"
                value={execCommand}
                onChange={(e) => setExecCommand(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleExecute();
                  }
                }}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="execTimeout">Timeout (seconds)</Label>
              <Input
                id="execTimeout"
                type="number"
                min={1}
                max={300}
                value={execTimeout}
                onChange={(e) => setExecTimeout(parseInt(e.target.value) || 30)}
                className="w-32"
              />
            </div>
            {execOutput && (
              <div className="space-y-2">
                <Label>Output</Label>
                <pre className="max-h-64 overflow-auto rounded-md border bg-muted p-4 font-mono text-xs whitespace-pre-wrap">
                  {execOutput}
                </pre>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExecTarget(null)}>Close</Button>
            <Button onClick={handleExecute} disabled={isExecuting || !execCommand.trim()}>
              {isExecuting ? 'Executing...' : 'Run'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Create sandbox dialog ────────────────────────────────────────────────────

function CreateSandboxDialog(props: { open: boolean; onOpenChange: (open: boolean) => void; onCreated: () => void }) {
  const toast = useToast();
  const [create, { isLoading: isCreating }] = useCreateSandboxMutation();
  const { data: repositories } = useGetRepositoriesQuery();
  const [selectedRepo, setSelectedRepo] = useState('');
  const { data: branches } = useGetBranchesQuery(selectedRepo ?? '', { skip: !selectedRepo });

  const repoList = asList<{ _id: string; name: string }>(repositories);
  const branchList = asList<{ _id: string; name: string }>(branches);

  const [form, setForm] = useState({
    name: '',
    repositoryId: '',
    branchId: '',
    factoryId: '',
    preset: 0,
    autoStopMinutes: 60,
  });

  const preset = RESOURCE_PRESETS[form.preset];

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    const body: CreateSandboxDto = {
      name: form.name.trim(),
      repositoryId: form.repositoryId || undefined,
      branchId: form.branchId || undefined,
      factoryId: form.factoryId || undefined,
      resources: preset ? { cpu: preset.cpu, memory: preset.memory, disk: preset.disk } : undefined,
      autoStopMinutes: form.autoStopMinutes,
    };
    try {
      await create(body).unwrap();
      props.onCreated();
      setForm({ name: '', repositoryId: '', branchId: '', factoryId: '', preset: 0, autoStopMinutes: 60 });
      props.onOpenChange(false);
    } catch (err) {
      toast.error(`Create failed: ${apiErrorMessage(err, 'Failed to create sandbox')}`);
    }
  };

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Create Sandbox</DialogTitle>
          <DialogDescription>Provision a new Daytona dev sandbox.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="my-sandbox" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Repository</Label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={form.repositoryId}
                onChange={(e) => {
                  setForm({ ...form, repositoryId: e.target.value, branchId: '' });
                  setSelectedRepo(e.target.value);
                }}
              >
                <option value="">— none —</option>
                {repoList.map((r) => (
                  <option key={r._id} value={r._id}>{r.name}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label>Branch</Label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={form.branchId}
                onChange={(e) => setForm({ ...form, branchId: e.target.value })}
                disabled={!form.repositoryId}
              >
                <option value="">— select —</option>
                {branchList.map((b) => (
                  <option key={b._id} value={b._id}>{b.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Resource preset</Label>
            <select
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={form.preset}
              onChange={(e) => setForm({ ...form, preset: parseInt(e.target.value) })}
            >
              {RESOURCE_PRESETS.map((p, idx) => (
                <option key={idx} value={idx}>{p.label}</option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <Label>Auto-stop (minutes)</Label>
            <Input
              type="number"
              min={0}
              value={form.autoStopMinutes}
              onChange={(e) => setForm({ ...form, autoStopMinutes: Number(e.target.value) || 0 })}
              className="w-40"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => props.onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={isCreating}>{isCreating ? 'Creating...' : 'Create Sandbox'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Branch link helper ───────────────────────────────────────────────────────

function BranchName(props: { repoId?: string; branchId?: string }) {
  const { data: branches } = useGetBranchesQuery(props.repoId ?? '', { skip: !props.repoId });
  const list = asList<{ _id: string; name: string }>(branches);
  const found = list.find((b) => b._id === props.branchId);
  if (!found) return null;
  return (
    <Link to={`/repositories/${props.repoId}/branches`} className="text-xs text-muted-foreground hover:text-primary">
      · {found.name}
    </Link>
  );
}